import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import BoltIcon from '@mui/icons-material/Bolt';

import { SelectChangeEvent } from '@mui/material/Select';

import { InputLabel, Select, FormControl } from '@mui/material';

import { Outlet, Link } from "react-router-dom";

import { useAuth } from '../components/Auth';
import { useGarageLocation } from '../components/Location';

export interface DropdownSelectionItem {
  title: string;
  onClick?: () => void | Promise<void>;
}

type ResponsiveAppBarProps = {
  pages:string[]
  settings: DropdownSelectionItem[]
  hideLocationDropdown?: boolean
}

function ResponsiveAppBar({pages, settings, hideLocationDropdown = false}: ResponsiveAppBarProps ) {
  const { isLoggedIn } = useAuth()!;
  const { garageLocation, garageLocations, garageLocationSelected, setTheGarageLocation } = useGarageLocation()!;

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLocationChange = (event: SelectChangeEvent<string>) => {
    setTheGarageLocation(event.target.value);
  };

  return (
    <AppBar position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <BoltIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
          <Typography
            variant="h6"
            noWrap
            component={Link}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            Chargeworks
          </Typography>

          {isLoggedIn && !hideLocationDropdown && (
            <FormControl sx={{ m: 1, minWidth: 120 }}>
              <InputLabel>Location</InputLabel>
              <Select
                labelId="location-select-label"
                id="location-select"
                value={garageLocationSelected ? garageLocation : ''}
                onChange={handleLocationChange}
                label="Location"
              >
                {garageLocations.map((location) => (
                  <MenuItem value={location} key={location}>{location}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  component={Link}
                  to={`/${page.toLowerCase().replace('_', '-')}`} // assuming your page routes are named after the page strings, converted to lowercase
                  key={page}
                  onClick={handleCloseNavMenu}
                >
                  <Typography textAlign="center">{page.replace('_', ' ')}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <BoltIcon sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }} />
          <Typography
            variant="h5"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            Chargeworks
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <Button
                component={Link}
                to={`/${page.toLowerCase().replace('_', '-')}`} // assuming your page routes are named after the page strings, converted to lowercase
                key={page}
                onClick={handleCloseNavMenu}
                sx={{ my: 2, color: 'white', display: 'block' }}
              >
                {page.replace('_', ' ')}
              </Button>
            ))}
          </Box>

          {isLoggedIn && (
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Open settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar alt="Remy Sharp" src="/static/images/avatar/2.jpg" />
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting) => (
                  <MenuItem key={setting.title} onClick={() => {setting.onClick && setting.onClick(); handleCloseUserMenu()}}>
                    <Typography textAlign="center">{setting.title}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Toolbar>
        <Outlet />
      </Container>
    </AppBar>
  );
}
export default ResponsiveAppBar;